import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';

function displayOrFallback(value, fallback) {
  const s = value != null && String(value).trim() !== '' ? String(value).trim() : '';
  return s || fallback;
}

function formatAddress(data) {
  if (!data) return '';
  const parts = [data.address, data.city, data.state, data.pinCode].filter(
    (p) => p != null && String(p).trim() !== ''
  );
  return parts.join(', ');
}

function CameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9 3L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-3.17L15 3H9zm3 13c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-2c1.65 0 3-1.35 3-3s-1.35-3-3-3-3 1.35-3 3 1.35 3 3 3z"
      />
    </svg>
  );
}

function ShopInfoField({ label, value }) {
  return (
    <div className="shop-info-field">
      <p className="shop-info-field-label">{label}</p>
      <p className="shop-info-field-value">{value}</p>
    </div>
  );
}

function ShopInfoPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [banner, setBanner] = useState({ kind: '', text: '' });
  const [avatarBroken, setAvatarBroken] = useState(false);

  const loadShop = useCallback(async (signal) => {
    setLoading(true);
    setBanner({ kind: '', text: '' });
    try {
      const data = await webApi.getShopInfo({ signal });
      if (signal.aborted) return;
      setShop(data);
      setAvatarBroken(false);
    } catch (err) {
      if (signal.aborted || isCanceledRequest(err)) return;
      setBanner({ kind: 'error', text: err.message || 'Failed to load shop information' });
      setShop(null);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    loadShop(ac.signal);
    return () => ac.abort();
  }, [loadShop]);

  const pickPhoto = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      if (file) setBanner({ kind: 'error', text: 'Please choose a JPEG, PNG, or WebP image.' });
      return;
    }

    setUploading(true);
    setBanner({ kind: '', text: '' });
    try {
      const fileType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
      const { uploadUrl, fileKey } = await webApi.getProfileUploadUrl({
        fileType,
        fileSize: file.size
      });
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': fileType }
      });
      if (!putRes.ok) {
        throw new Error('Upload failed. Check your connection and try again.');
      }
      const { profilePictureUrl } = await webApi.confirmProfilePictureUpload({ fileKey });
      setShop((prev) => (prev ? { ...prev, profilePictureUrl } : prev));
      setAvatarBroken(false);
      setBanner({ kind: 'success', text: 'Shop picture updated successfully.' });
    } catch (err) {
      setBanner({ kind: 'error', text: err.message || 'Failed to upload image' });
    } finally {
      setUploading(false);
    }
  };

  const name = shop?.name || '';
  const category = displayOrFallback(shop?.businessType, 'Not specified');
  const gst = displayOrFallback(shop?.gstNumber, 'Not provided');
  const businessTypeRow = displayOrFallback(
    shop?.gstinVerifiedName || shop?.businessType,
    'Not specified'
  );
  const phone = displayOrFallback(shop?.phone, 'Not provided');
  const email = displayOrFallback(shop?.email, 'Not provided');
  const address = displayOrFallback(formatAddress(shop), 'Not provided');
  const poolProvider = displayOrFallback(shop?.poolPaymentProvider, 'razorpay').toUpperCase();
  const reviewInclusion = shop?.reviewInclusionEnabled ? 'Enabled' : 'Disabled';
  const picUrl = shop?.profilePictureUrl;
  const initial = (name.trim()[0] || '?').toUpperCase();

  return (
    <div className="shop-info-page">
      <header className="shop-info-topbar">
        <button
          type="button"
          className="shop-info-back"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="shop-info-topbar-title">Shop Information</h1>
        <span className="shop-info-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="shop-info-body">
        {banner.text ? (
          <p className={`shop-info-banner shop-info-banner--${banner.kind}`} role="status">
            {banner.text}
          </p>
        ) : null}

        {loading ? (
          <p className="shop-info-loading">Loading…</p>
        ) : shop ? (
          <>
            <section className="shop-info-profile-panel">
              <div className="shop-info-avatar-wrap">
                {picUrl && !avatarBroken ? (
                  <img
                    className="shop-info-avatar"
                    src={picUrl}
                    alt=""
                    onError={() => setAvatarBroken(true)}
                  />
                ) : (
                  <span className="shop-info-avatar shop-info-avatar--placeholder" aria-hidden="true">
                    {initial}
                  </span>
                )}
                <button
                  type="button"
                  className="shop-info-camera-btn"
                  onClick={pickPhoto}
                  disabled={uploading}
                  aria-label="Change shop photo"
                >
                  <CameraIcon />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="shop-info-file-input"
                  onChange={onFileChange}
                  aria-hidden="true"
                />
              </div>
              <p className="shop-info-profile-name">{displayOrFallback(name, '—')}</p>
            </section>

            <article className="shop-info-card">
              <h2 className="shop-info-card-title">Shop Details</h2>
              <div className="shop-info-card-rule" />
              <div className="shop-info-card-body">
                <ShopInfoField label="Shop Name" value={displayOrFallback(name, '—')} />
                <ShopInfoField label="Category" value={category} />
                <ShopInfoField label="GST Number" value={gst} />
                <ShopInfoField label="Business Type" value={businessTypeRow} />
                <ShopInfoField label="Pool Payment Provider" value={poolProvider} />
                <ShopInfoField label="Pool Review Inclusion" value={reviewInclusion} />
              </div>
            </article>

            <article className="shop-info-card">
              <h2 className="shop-info-card-title">Contact Details</h2>
              <div className="shop-info-card-rule" />
              <div className="shop-info-card-body">
                <ShopInfoField label="Phone Number" value={phone} />
                <ShopInfoField label="Email ID" value={email} />
                <ShopInfoField label="Address" value={address} />
              </div>
            </article>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default ShopInfoPage;
